import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Bell, Mail, MessageSquare, CheckCircle2, AlertTriangle, Loader2, History } from 'lucide-react';
import api from '../lib/api';
// P_USER_WELCOME_DISPATCH_2.1 — proper UTC→local conversion.
import { fmtLocalShort } from '../utils/localTime';

/**
 * P_DELIVERY_CHANNELS_5.0c — Communication Preferences page.
 *
 * UI-only. Talks exclusively to /api/communications/preferences/me.
 * Backend enforces guardrails (no-channel, unverified-email,
 * unverified-sms) — UI surfaces errors via toasts.
 */

const STATUS_TONE = {
    verified:    'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
    pending:     'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300',
    unavailable: 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300',
    disabled:    'bg-muted text-muted-foreground',
};

function ChannelBadge({ status, label, testid }) {
    return (
        <Badge variant="outline" className={STATUS_TONE[status] || ''} data-testid={testid}>
            {label}
        </Badge>
    );
}

export default function CommunicationPreferencesPage() {
    const { t } = useTranslation();
    const [prefs, setPrefs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [emailInput, setEmailInput] = useState('');
    const [phoneInput, setPhoneInput] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [smsCode, setSmsCode] = useState('');
    const [emailStartStatus, setEmailStartStatus] = useState(null);
    const [smsStartStatus, setSmsStartStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    const [recent, setRecent] = useState({ items: [], loading: true });

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/communications/preferences/me');
            setPrefs(r.data);
            setEmailInput(r.data.email || '');
            setPhoneInput(r.data.phone || '');
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }, []);

    const reloadRecent = useCallback(async () => {
        setRecent((s) => ({ ...s, loading: true }));
        try {
            const r = await api.get('/communications/preferences/me/recent-deliveries', { params: { limit: 10 } });
            setRecent({ items: r.data?.items || [], loading: false });
        } catch (_e) {
            setRecent({ items: [], loading: false });
        }
    }, []);

    useEffect(() => { reload(); reloadRecent(); }, [reload, reloadRecent]);

    const updateChannels = async (dtype, channel, value) => {
        if (!prefs) return;
        const updated = { ...prefs.channels[dtype], [channel]: value };
        setBusy(true);
        setError(null);
        try {
            await api.put('/communications/preferences/me', { [dtype]: updated });
            await reload();
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    const startEmail = async () => {
        setBusy(true); setError(null); setEmailStartStatus(null);
        try {
            const r = await api.post('/communications/preferences/me/verify/email/start', { address: emailInput });
            setEmailStartStatus(r.data.status);
            await reload();
        } catch (e) { setError(e.response?.data?.detail || e.message); }
        finally { setBusy(false); }
    };
    const confirmEmail = async () => {
        setBusy(true); setError(null);
        try {
            await api.post('/communications/preferences/me/verify/email/confirm', { code: emailCode });
            setEmailCode(''); setEmailStartStatus(null);
            await reload();
        } catch (e) { setError(e.response?.data?.detail || e.message); }
        finally { setBusy(false); }
    };
    const startSms = async () => {
        setBusy(true); setError(null); setSmsStartStatus(null);
        try {
            const r = await api.post('/communications/preferences/me/verify/sms/start', { address: phoneInput });
            setSmsStartStatus(r.data.status);
            await reload();
        } catch (e) { setError(e.response?.data?.detail || e.message); }
        finally { setBusy(false); }
    };
    const confirmSms = async () => {
        setBusy(true); setError(null);
        try {
            await api.post('/communications/preferences/me/verify/sms/confirm', { code: smsCode });
            setSmsCode(''); setSmsStartStatus(null);
            await reload();
        } catch (e) { setError(e.response?.data?.detail || e.message); }
        finally { setBusy(false); }
    };

    if (loading) return (
        <div className="p-8 text-center text-muted-foreground" data-testid="prefs-loading">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        </div>
    );
    if (!prefs) return null;

    const emailStatus = prefs.email_verified ? 'verified'
        : prefs.email ? 'pending' : 'unavailable';
    const smsStatus = prefs.phone_verified ? 'verified'
        : prefs.phone ? 'pending' : 'unavailable';

    return (
        <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-4" data-testid="comm-preferences-page">
            <div>
                <h1 className="text-2xl font-semibold text-foreground">{t('commPrefs.title', 'Communication Preferences')}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t('commPrefs.subtitle', 'Choose how YulOnTap reaches you. In-app delivery is always available.')}</p>
            </div>

            {error ? (
                <Card className="p-3 border-rose-200 dark:border-rose-900 bg-rose-50/40 dark:bg-rose-950/20 flex items-start gap-2" data-testid="prefs-error">
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-600 dark:text-rose-400" />
                    <span className="text-sm text-foreground">{String(error)}</span>
                </Card>
            ) : null}

            {/* In-App */}
            <Card className="p-4 space-y-2" data-testid="prefs-in-app-section">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Bell className="w-5 h-5 text-foreground" />
                        <span className="font-medium text-foreground">{t('commPrefs.inApp.title', 'In-App Notifications')}</span>
                    </div>
                    <ChannelBadge status="verified" label={t('commPrefs.status.always', 'Always available')} testid="prefs-in-app-status" />
                </div>
                <p className="text-xs text-muted-foreground">{t('commPrefs.inApp.note', 'In-app delivery is always on so you never miss an important message.')}</p>
            </Card>

            {/* Email */}
            <Card className="p-4 space-y-3" data-testid="prefs-email-section">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-foreground" />
                        <span className="font-medium text-foreground">{t('commPrefs.email.title', 'Email')}</span>
                    </div>
                    <ChannelBadge status={emailStatus}
                        label={t(`commPrefs.status.${emailStatus}`, emailStatus)}
                        testid="prefs-email-status" />
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                    <Input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                        placeholder="you@example.com" data-testid="prefs-email-input" />
                    <Button variant="outline" size="sm" disabled={busy || !emailInput}
                        onClick={startEmail} data-testid="prefs-email-send">
                        {t('commPrefs.email.sendCode', 'Send code')}
                    </Button>
                </div>
                {emailStartStatus === 'provider_unavailable' ? (
                    <p className="text-xs text-rose-600 dark:text-rose-400" data-testid="prefs-email-unavailable">
                        {t('commPrefs.email.unavailable', 'Email service unavailable. Contact your administrator.')}
                    </p>
                ) : null}
                {emailStartStatus === 'sent' ? (
                    <div className="flex gap-2" data-testid="prefs-email-confirm-row">
                        <Input value={emailCode} onChange={(e) => setEmailCode(e.target.value)}
                            placeholder="000000" maxLength={6} data-testid="prefs-email-code" />
                        <Button size="sm" disabled={busy || emailCode.length < 4}
                            onClick={confirmEmail} data-testid="prefs-email-confirm">
                            {t('commPrefs.email.verify', 'Verify')}
                        </Button>
                    </div>
                ) : null}
                <ChannelToggleRow label={t('commPrefs.email.receiveWelcome', 'Receive Daily Welcome by email')}
                    checked={!!prefs.channels.welcome.email} disabled={busy || !prefs.email_verified}
                    onChange={(v) => updateChannels('welcome', 'email', v)}
                    testid="prefs-email-welcome-toggle" />
                <ChannelToggleRow label={t('commPrefs.email.receiveCampaign', 'Receive Campaigns by email')}
                    checked={!!prefs.channels.campaign.email} disabled={busy || !prefs.email_verified}
                    onChange={(v) => updateChannels('campaign', 'email', v)}
                    testid="prefs-email-campaign-toggle" />
            </Card>

            {/* SMS */}
            <Card className="p-4 space-y-3" data-testid="prefs-sms-section">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-foreground" />
                        <span className="font-medium text-foreground">{t('commPrefs.sms.title', 'SMS')}</span>
                    </div>
                    <ChannelBadge status={smsStatus}
                        label={t(`commPrefs.status.${smsStatus}`, smsStatus)}
                        testid="prefs-sms-status" />
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                    <Input type="tel" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)}
                        placeholder="+14165550100" data-testid="prefs-phone-input" />
                    <Button variant="outline" size="sm" disabled={busy || !phoneInput}
                        onClick={startSms} data-testid="prefs-sms-send">
                        {t('commPrefs.sms.sendCode', 'Send code')}
                    </Button>
                </div>
                {smsStartStatus === 'provider_unavailable' ? (
                    <p className="text-xs text-rose-600 dark:text-rose-400" data-testid="prefs-sms-unavailable">
                        {t('commPrefs.sms.unavailable', 'SMS service unavailable. Contact your administrator.')}
                    </p>
                ) : null}
                {smsStartStatus === 'sent' ? (
                    <div className="flex gap-2" data-testid="prefs-sms-confirm-row">
                        <Input value={smsCode} onChange={(e) => setSmsCode(e.target.value)}
                            placeholder="000000" maxLength={6} data-testid="prefs-sms-code" />
                        <Button size="sm" disabled={busy || smsCode.length < 4}
                            onClick={confirmSms} data-testid="prefs-sms-confirm">
                            {t('commPrefs.sms.verify', 'Verify')}
                        </Button>
                    </div>
                ) : null}
                <ChannelToggleRow label={t('commPrefs.sms.receiveWelcome', 'Receive Daily Welcome by SMS')}
                    checked={!!prefs.channels.welcome.sms} disabled={busy || !prefs.phone_verified}
                    onChange={(v) => updateChannels('welcome', 'sms', v)}
                    testid="prefs-sms-welcome-toggle" />
                <ChannelToggleRow label={t('commPrefs.sms.receiveCampaign', 'Receive Campaigns by SMS')}
                    checked={!!prefs.channels.campaign.sms} disabled={busy || !prefs.phone_verified}
                    onChange={(v) => updateChannels('campaign', 'sms', v)}
                    testid="prefs-sms-campaign-toggle" />
            </Card>

            {/* P_DELIVERY_CHANNELS_5.0f — Recent Deliveries (read-only support UX) */}
            <Card className="p-4 space-y-3" data-testid="prefs-recent-deliveries-section">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <History className="w-5 h-5 text-foreground" />
                        <span className="font-medium text-foreground">
                            {t('commPrefs.recent.title', 'Recent Deliveries')}
                        </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                        {t('commPrefs.recent.subtitle', 'Last 10 attempts')}
                    </span>
                </div>
                {recent.loading ? (
                    <div className="py-2 text-center" data-testid="prefs-recent-loading">
                        <Loader2 className="w-4 h-4 mx-auto animate-spin text-muted-foreground" />
                    </div>
                ) : recent.items.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2" data-testid="prefs-recent-empty">
                        {t('commPrefs.recent.empty', 'No recent deliveries yet.')}
                    </p>
                ) : (
                    <ul className="divide-y divide-border" data-testid="prefs-recent-list">
                        {recent.items.map((it) => (
                            <li key={it.id}
                                className="flex items-center justify-between gap-3 py-1.5 text-xs"
                                data-testid={`prefs-recent-row-${it.id}`}>
                                <span className="font-mono text-foreground" data-testid={`prefs-recent-reason-${it.id}`}>
                                    {it.reason}
                                </span>
                                <span className="text-muted-foreground tabular-nums">
                                    {fmtLocalShort(it.delivered_at || it.sent_at || it.failed_at || it.queued_at, t('common.locale')) || '—'}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </div>
    );
}

const ChannelToggleRow = ({ label, checked, disabled, onChange, testid }) => (
    <label className={`flex items-center justify-between gap-2 text-sm py-1 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
        <span className="text-foreground">{label}</span>
        <input type="checkbox" checked={!!checked} disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
            data-testid={testid} />
    </label>
);
